import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor,
  CheckCircle2,
  XCircle,
  Hourglass,
  Eye,
  Trash2,
  Bell,
  AlertTriangle,
} from 'lucide-react';
import { getWorkers, getPendingWorkers, approveWorker, deleteWorker } from '../services/api';
import AutoRefreshControl from '../components/AutoRefreshControl';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StatTile } from '../components/ui/stat-tile';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '../components/ui/modal';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tooltip } from '../components/ui/tooltip';
import { cn } from '../lib/utils';

const STATUS_BADGE = {
  online: { variant: 'success', icon: CheckCircle2, label: 'Online' },
  approved: { variant: 'secondary', icon: CheckCircle2, label: 'Approved' },
  pending: { variant: 'warning', icon: Hourglass, label: 'Pending' },
  offline: { variant: 'outline', icon: Monitor, label: 'Offline' },
  rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
};

function StatusBadge({ status }) {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.offline;
  const Icon = meta.icon;
  return (
    <Badge variant={meta.variant} className="capitalize">
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

function formatLastSeen(lastSeen) {
  if (!lastSeen) return '—';
  const date = new Date(lastSeen);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return date.toLocaleString();
}

function WorkersPage() {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [pendingWorkers, setPendingWorkers] = useState([]);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  const loadData = async () => {
    try {
      const [allWorkers, pending] = await Promise.all([getWorkers(), getPendingWorkers()]);
      setWorkers(allWorkers);
      setPendingWorkers(pending);
    } catch (error) {
      console.error('Failed to load workers:', error);
    }
  };

  const autoRefresh = useAutoRefresh(loadData, 30000, true);

  useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async (worker, approve) => {
    try {
      await approveWorker(worker.id, approve);
      setApprovalDialogOpen(false);
      setSelectedWorker(null);
      loadData();
    } catch (error) {
      console.error('Failed to approve/reject worker:', error);
    }
  };

  const handleDelete = async (worker) => {
    try {
      await deleteWorker(worker.id);
      setDeleteDialogOpen(false);
      setSelectedWorker(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete worker:', error);
    }
  };

  const onlineCount = workers.filter((w) => w.status === 'online').length;
  const offlineCount = workers.filter(
    (w) => w.status === 'offline' || w.status === 'approved'
  ).length;

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Worker Management
          </h1>
          <p className="text-sm text-muted-foreground">
            Approve, monitor, and manage every endpoint connected to the DLP system.
          </p>
        </div>
        <AutoRefreshControl {...autoRefresh} />
      </div>

      {/* Pending alert */}
      {pendingWorkers.length > 0 && (
        <Alert variant="warning" className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Bell className="h-4 w-4 text-warning" />
            <AlertDescription className="text-foreground">
              <span className="font-semibold">{pendingWorkers.length}</span> worker
              {pendingWorkers.length > 1 ? 's' : ''} waiting for approval
            </AlertDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedWorker(pendingWorkers[0]);
              setApprovalDialogOpen(true);
            }}
          >
            Review
          </Button>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Online" value={onlineCount} icon={CheckCircle2} tone="success" />
        <StatTile label="Offline" value={offlineCount} icon={Monitor} tone="default" />
        <StatTile label="Pending" value={pendingWorkers.length} icon={Hourglass} tone="warning" />
        <StatTile label="Total Workers" value={workers.length} icon={Monitor} tone="primary" />
      </div>

      {/* Workers table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Workers</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Worker Name</TableHead>
                <TableHead>Computer</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>Last Seen</TableHead>
                <TableHead>Version</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workers.map((worker) => (
                <TableRow
                  key={worker.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/worker/${worker.id}`)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <StatusBadge status={worker.status} />
                  </TableCell>
                  <TableCell className="font-medium text-foreground">
                    {worker.worker_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{worker.computer_name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {worker.ip_address}
                  </TableCell>
                  <TableCell className="text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                    {formatLastSeen(worker.last_seen)}
                  </TableCell>
                  <TableCell className="text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                    {worker.client_version || '—'}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <Tooltip label="View details" side="left">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => navigate(`/worker/${worker.id}`)}
                        >
                          <Eye />
                        </Button>
                      </Tooltip>
                      {worker.status === 'pending' && (
                        <Tooltip label="Approve / reject" side="left">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-warning hover:text-warning"
                            onClick={() => {
                              setSelectedWorker(worker);
                              setApprovalDialogOpen(true);
                            }}
                          >
                            <Bell />
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip label="Delete worker" side="left">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setSelectedWorker(worker);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 />
                        </Button>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {workers.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className={cn('py-12 text-center text-sm text-muted-foreground')}
                  >
                    No workers registered yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approval modal */}
      <Modal
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
      >
        <ModalHeader>
          <ModalTitle>Worker Approval</ModalTitle>
          <ModalDescription>
            Review the device details before granting monitoring access.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {selectedWorker && (
            <dl className="grid grid-cols-3 gap-y-3 text-sm">
              <dt className="text-muted-foreground">Worker name</dt>
              <dd className="col-span-2 font-medium text-foreground">
                {selectedWorker.worker_name}
              </dd>
              <dt className="text-muted-foreground">Computer name</dt>
              <dd className="col-span-2 text-foreground">{selectedWorker.computer_name}</dd>
              <dt className="text-muted-foreground">IP address</dt>
              <dd className="col-span-2 font-mono text-xs text-foreground">
                {selectedWorker.ip_address}
              </dd>
              <dt className="text-muted-foreground">Operating system</dt>
              <dd className="col-span-2 text-foreground">{selectedWorker.operating_system}</dd>
              <dt className="text-muted-foreground">Registered</dt>
              <dd className="col-span-2 text-foreground">
                {selectedWorker.registration_date
                  ? new Date(selectedWorker.registration_date).toLocaleString()
                  : '—'}
              </dd>
            </dl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setApprovalDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => handleApprove(selectedWorker, false)}
          >
            Reject
          </Button>
          <Button onClick={() => handleApprove(selectedWorker, true)}>Approve</Button>
        </ModalFooter>
      </Modal>

      {/* Delete modal */}
      <Modal open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <ModalHeader>
          <ModalTitle>Delete worker</ModalTitle>
          <ModalDescription>This action cannot be undone.</ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertDescription>
              This will permanently delete the worker and all associated monitoring data.
            </AlertDescription>
          </Alert>
          {selectedWorker && (
            <dl className="grid grid-cols-3 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Worker name</dt>
              <dd className="col-span-2 font-medium text-foreground">
                {selectedWorker.worker_name}
              </dd>
              <dt className="text-muted-foreground">Computer</dt>
              <dd className="col-span-2 text-foreground">{selectedWorker.computer_name}</dd>
              <dt className="text-muted-foreground">IP</dt>
              <dd className="col-span-2 font-mono text-xs text-foreground">
                {selectedWorker.ip_address}
              </dd>
            </dl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => handleDelete(selectedWorker)}>
            <Trash2 />
            Delete worker
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default WorkersPage;
